import { SmoothLink as Link } from "@/components/navigation";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EntityCard } from "@/components/shared/EntityCard";
import { generateAltText, getEntityImageUrl } from "@/lib/image-utils";
import { Star, Clock, Languages, User, ArrowRight, Video, DollarSign } from "lucide-react";
import type { Doctor } from "@/types/schema";
import { useAnalyticsContext } from "@/components/common/AnalyticsProvider";

interface DoctorCardProps {
  doctor: Doctor;
}

export function DoctorCard({ doctor }: DoctorCardProps) {
  const { trackDoctorView } = useAnalyticsContext();
  const altText = generateAltText(doctor.name, "doctor");

  const imageUrl = getEntityImageUrl(doctor.image_url, doctor.name, "person", 400, 400);

  return (
    <EntityCard
      imageUrl={imageUrl}
      imageAlt={altText}
      title={doctor.name}
      subtitle={doctor.title || undefined}
      isFeatured={doctor.is_featured || false}
      featuredLabel="Top Rated"
      aspectRatio="square"
      fallbackIcon={<User className="w-16 h-16 text-secondary-foreground/30" />}
      badges={[{ label: doctor.specialty, variant: "outline" }]}
    >
      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
        <div className="flex items-center justify-center gap-2 text-muted-foreground">
          <Clock className="w-4 h-4" />
          <span>{doctor.experience_years}+ Years</span>
        </div>
        <div className="flex items-center justify-center gap-2 text-accent">
          <Star className="w-4 h-4 fill-accent" />
          <span>{doctor.rating} ({doctor.review_count})</span>
        </div>
      </div>

      {/* Consultation Price */}
      {doctor.offers_online_consultation && doctor.online_consultation_fee && (
        <div className="flex items-center justify-center gap-2 mb-4 p-2 rounded-lg bg-primary/5 border border-primary/10">
          <Video className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium text-primary">
            Online Consultation: ${doctor.online_consultation_fee}
          </span>
        </div>
      )}

      {/* Qualifications */}
      {doctor.qualifications && doctor.qualifications.length > 0 && (
        <div className="flex flex-wrap justify-center gap-1 mb-4">
          {doctor.qualifications.slice(0, 3).map((qual) => (
            <Badge key={qual} variant="secondary" className="text-xs">
              {qual}
            </Badge>
          ))}
          {doctor.qualifications.length > 3 && (
            <Badge variant="secondary" className="text-xs">
              +{doctor.qualifications.length - 3} more
            </Badge>
          )}
        </div>
      )}

      {/* Languages */}
      {doctor.languages && doctor.languages.length > 0 && (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-4">
          <Languages className="w-4 h-4" />
          <span>{doctor.languages.join(", ")}</span>
        </div>
      )}

      <Link to={`/doctors/${doctor.slug}`}>
        <Button 
          variant="outline" 
          className="w-full rounded-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
          onClick={() => trackDoctorView(doctor.name, doctor.specialty)}
        >
          View Profile
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </Link>
    </EntityCard>
  );
}
